import React, { memo, useState, useEffect } from 'react';
import { Box, Typography, Alert, IconButton } from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { getMapSize, isMobile } from '../utils/platform';
import { apiService } from '../services/apiService';

/**
 * Static map component using Google Maps Static API via our backend proxy
 * Used primarily on mobile devices where interactive maps may have issues
 */
function StaticMapDisplay({
  location = null,
  currentLocation = null,
  width = '100%',
  height = '300px',
  zoom = 14,
  mapType = 'roadmap',
  showWhenEmpty = true,
  customSize = null,
  showRefresh = false,
  onRefresh = null
}) {
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Determine which location to display
  const displayLocation = location || currentLocation;

  // Get appropriate map size
  const mapSize = getMapSize(customSize);

  // Generate static map from our API proxy
  useEffect(() => {
    if (!displayLocation) {
      if (showWhenEmpty) {
        setError('No location data available');
      }
      return;
    }

    setLoading(true);
    setError(null);

    // Prepare request for static map API
    const staticMapRequest = {
      center: `${displayLocation.coordinates.lat},${displayLocation.coordinates.lng}`,
      zoom: zoom,
      size: mapSize,
      maptype: mapType,
      markers: `color:red|${displayLocation.coordinates.lat},${displayLocation.coordinates.lng}`,
      scale: isMobile() ? 2 : 1, // Higher DPI on mobile
      format: 'png'
    };

    // Call our API proxy endpoint
    apiService.getStaticMap(staticMapRequest, { 
      responseType: 'blob',
      headers: {
        'Accept': 'image/png,image/jpeg,image/*'
      }
    })
      .then((response) => {
        // Create blob URL from response
        const blob = new Blob([response.data], { type: response.headers['content-type'] || 'image/png' });
        const url = URL.createObjectURL(blob);
        setImageUrl(url);
        setError(null);
      })
      .catch((err) => {
        console.error('Failed to load static map:', err);
        setError(err.response?.data?.error || 'Failed to load map');
        setImageUrl(null);
      })
      .finally(() => {
        setLoading(false);
      });

    // Cleanup function to revoke blob URL
    return () => {
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [displayLocation, zoom, mapType, mapSize, refreshKey]);

  const handleRefresh = () => {
    if (onRefresh) {
      onRefresh();
    }
    setRefreshKey(prev => prev + 1);
  };

  // Don't show anything if no location and showWhenEmpty is false
  if (!showWhenEmpty && !displayLocation) {
    return null;
  }

  return (
    <Box sx={{ width, height, position: 'relative' }}>
      {loading && (
        <Box 
          sx={{ 
            width: '100%', 
            height: '100%',
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            backgroundColor: 'grey.50',
            border: '1px solid',
            borderColor: 'grey.300',
            borderRadius: 1
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Loading map...
          </Typography>
        </Box>
      )}
      
      {error && (
        <Box sx={{ width: '100%', height: '100%' }}>
          <Alert severity="warning" sx={{ height: '100%', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2">
                {error}
              </Typography>
              {showRefresh && (
                <IconButton
                  size="small"
                  onClick={handleRefresh}
                  color="inherit"
                  title="Retry loading map"
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          </Alert>
        </Box>
      )}
      
      {imageUrl && !loading && !error && (
        <>
          <img
            src={imageUrl}
            alt="Map location"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              border: '1px solid #e0e0e0',
              borderRadius: '4px',
              display: 'block'
            }}
            onError={() => {
              setError('Failed to display map image');
              setImageUrl(null);
            }}
          />
          
          {showRefresh && (
            <IconButton
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 1)',
                }
              }}
              size="small"
              onClick={handleRefresh}
              title="Refresh map"
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          )}
        </>
      )}
      
      {!displayLocation && showWhenEmpty && !loading && (
        <Box 
          sx={{ 
            width: '100%', 
            height: '100%',
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            backgroundColor: 'grey.100',
            border: '1px solid',
            borderColor: 'grey.300',
            borderRadius: 1
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No location data available
          </Typography>
        </Box>
      )}
    </Box>
  );
}

// Memoize to prevent unnecessary re-renders
export default memo(StaticMapDisplay, (prevProps, nextProps) => {
  const prevLoc = prevProps.location;
  const nextLoc = nextProps.location;
  const prevCurLoc = prevProps.currentLocation;
  const nextCurLoc = nextProps.currentLocation;
  
  return (
    prevProps.width === nextProps.width &&
    prevProps.height === nextProps.height &&
    prevProps.zoom === nextProps.zoom &&
    prevProps.mapType === nextProps.mapType &&
    prevProps.showWhenEmpty === nextProps.showWhenEmpty &&
    prevProps.customSize === nextProps.customSize &&
    prevProps.showRefresh === nextProps.showRefresh &&
    // Deep compare location objects
    (prevLoc === nextLoc || (
      prevLoc?.coordinates?.lat === nextLoc?.coordinates?.lat &&
      prevLoc?.coordinates?.lng === nextLoc?.coordinates?.lng
    )) &&
    (prevCurLoc === nextCurLoc || (
      prevCurLoc?.coordinates?.lat === nextCurLoc?.coordinates?.lat &&
      prevCurLoc?.coordinates?.lng === nextCurLoc?.coordinates?.lng
    ))
  );
});